/**
 * SocketService — WebSocket-Wrapper mit Auto-Reconnect & Event-Routing.
 *
 * WARUM: Reine ws-Library erfordert in jeder Datei Reconnect/Parse-Boilerplate.
 * Hier zentralisiert.
 */
import { eventBus } from '../core/eventBus.js';

const SERVER_URL = import.meta.env?.VITE_WS_URL || 'ws://localhost:3001';
const RECONNECT_DELAY = 2000;

class SocketService {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.shouldReconnect = false;
    this.reconnectTimer = null;
  }

  connect() {
    if (this.ws && this.ws.readyState <= 1) return; // bereits verbunden/verbindend

    this.shouldReconnect = true;
    try {
      this.ws = new WebSocket(SERVER_URL);
    } catch (err) {
      eventBus.emit('socket:error', { message: 'Cannot connect to server' });
      return;
    }

    this.ws.onopen = () => {
      this.connected = true;
      eventBus.emit('socket:open');
    };

    this.ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        // Routing: jeder Server-Message-Type wird zu einem EventBus-Event
        eventBus.emit(`server:${msg.type}`, msg);
        eventBus.emit('server:any', msg);
      } catch (err) {
        console.error('[Socket] Parse error:', err);
      }
    };

    this.ws.onclose = () => {
      this.connected = false;
      eventBus.emit('socket:closed');
      if (this.shouldReconnect) this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      eventBus.emit('socket:error', { message: 'Connection error' });
    };
  }

  scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) this.connect();
    }, RECONNECT_DELAY);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
  }

  send(type, payload = {}) {
    if (!this.connected || this.ws?.readyState !== 1) {
      eventBus.emit('socket:error', { message: 'Not connected' });
      return false;
    }
    this.ws.send(JSON.stringify({ type, ...payload }));
    return true;
  }
}

export const socketService = new SocketService();
